/**
 * Admin booking management, from docs/booking-api.swagger.json.
 *
 * `AdminBookingRowDto` already carries `paid`, `refunded` and `outstanding`
 * computed server-side — none of it is derived on the client.
 */

/** `BookingPaymentCategory` — enum [0..7]. Drives the whole "paid how" column. */
export const PAYMENT_CATEGORY = {
  usedBenefit: 0,
  paidOnline: 1,
  paidOffline: 2,
  freeEvent: 3,
  comped: 4,
  awaitingPayment: 5,
  refunded: 6,
  needsReview: 7,
} as const;

export const BOOKING_STATUS = {
  pending: 0,
  confirmed: 1,
  cancelled: 2,
} as const;

/** `AdminBookingRowDto`. */
export interface AdminBookingRow {
  id: string;
  createdAt: string | null;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;

  timeslotId: string | null;
  eventId: string | null;
  eventName: string | null;
  locationName: string | null;
  timeslotStartTime: string | null;
  timeslotEndTime: string | null;

  status: number | null;
  checkedInAt: string | null;
  comments: string | null;
  isFirstTimeAtEvent: boolean;

  paymentCategory: number | null;
  price: number | null;
  paid: number | null;
  refunded: number | null;
  outstanding: number | null;
  paymentMethods: number[] | null;
  hasOfflinePayment: boolean;

  userSubscriptionId: string | null;
  benefitNameEn: string | null;
  benefitNameEs: string | null;
  subscriptionNameEn: string | null;
  subscriptionNameEs: string | null;

  compedAt: string | null;
  compedReason: string | null;

  /**
   * Set only on a booking of a packaged event.
   *
   * There is no `bookedPrice` here — that lives on `EventBookingDto`, the
   * per-time-slot shape. On this row `price` is the one price there is.
   */
  packageId: string | null;
  packageNameEn: string | null;
  packageNameEs: string | null;
}

/** `PagedAdminBookingsDto`. */
export interface PagedAdminBookings {
  total: number;
  page: number;
  size: number;
  bookings: AdminBookingRow[];
}

/** `PurchasePaymentDto` — one payment against a purchase. */
export interface BookingPayment {
  id: string;
  createdAt: string | null;
  purchaseId: string | null;
  amount: number | null;
  originalAmount: number | null;
  refundedAmount: number | null;
  failed: boolean;
  isFailed: boolean;
  isPending: boolean;
  message: string | null;
  method: number | null;
  isOffline: boolean;
  offlineReference: string | null;
  adminNotes: string | null;
  recordedAt: string | null;
  recordedByName: string | null;
  voidedAt: string | null;
  voidedByName: string | null;
  internalReference: string | null;
  /** Added client-side when flattening across purchases. */
  purchaseAmount?: number | null;
}

export interface BookingPurchase {
  id: string;
  createdAt: string | null;
  amount: number | null;
  amountPaid: number | null;
  refundAmount: number | null;
  cancelled: boolean;
  payments: BookingPayment[] | null;
}

/** `BookingMoveDto`. */
export interface BookingMove {
  movedAt: string | null;
  fromStartTime: string | null;
  toStartTime: string | null;
  movedByName: string | null;
  reason: string | null;
  capacityIncreased: boolean;
}

/**
 * `BookingPackageChangeDto` — one move between packages.
 *
 * The prices are snapshots of what each package cost at the time, not a
 * reference to what it costs now; render them as given.
 */
export interface BookingPackageChange {
  changedAt: string;
  /** Absent on the first change: the booking was on no package before it. */
  fromPackageNameEn: string | null;
  fromPackageNameEs: string | null;
  fromPrice: number | null;
  toPackageNameEn: string | null;
  toPackageNameEs: string | null;
  toPrice: number;
  changedByName: string | null;
  reason: string | null;
}

/** `BookingDetailDto`. */
export interface BookingDetail {
  booking: AdminBookingRow;
  purchases: BookingPurchase[] | null;
  additionalInformation1Response: string | null;
  additionalInformation2Response: string | null;
  moves: BookingMove[] | null;
  packageChanges: BookingPackageChange[] | null;
}
